/**
 * Responsibility: search any card, not just the ones a population report
 * happens to cover.
 *
 * The discovery page starts from a fixed universe and screens it down. This
 * one starts from a phrase, because the cards worth asking about - a Topps
 * Ohtani rookie, a signed Curry - are not in any universe this system holds.
 * Everything here comes from PriceCharting and its sports sibling, which
 * between them cover sports, autographs and trading cards, and which report
 * completed sales rather than what somebody is asking.
 *
 * **Laid out like every other page**: the tabs are the card's own first
 * line, then the figures, then one row carrying every control, then the
 * results. It used to
 * stack five things above the table - tabs, the query row, a line explaining
 * the mode, two wrapped rows of filters, and the figures - which left about
 * a row and a half of the window for the results the page exists to show.
 * The filters went behind a button, the mode's explanation became a tooltip
 * on the control it explains, and the rest is the shared `ShelfToolbar`.
 *
 * Nothing loads until a search is submitted. Each result costs a page read
 * from a free catalogue, so the page opens empty with examples rather than
 * running a default query nobody asked for.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import { MetricCard, PageContainer, PageTabs } from '@kit/components';
import { MarketCardDialog } from '../components/market/MarketCardDialog';
import { MarketTable } from '../components/market/MarketTable';
import { SearchBar } from '../components/market/SearchBar';
import { SearchFilterButton } from '../components/market/SearchFilterButton';
import { queryFrom, useSearchDraft } from '../components/market/searchDraft';
import type { SearchDraft } from '../components/market/searchDraft';
import {
  useAddToCollection,
  useForgetSearch,
  useMarkSearchRun,
  useMarketSearch,
  useSaved,
  useSaveSearch,
  useToggleLike,
  useToggleWatch,
} from '../api/hooks/useMarket';
import { useAddHoldingWithTags } from '../api/hooks/useTags';
import { AddHoldingDialog } from '../components/collection/AddHoldingDialog';
import { CardTagDialog, type TaggableCard } from '../components/collection/CardTagDialog';
import { SavedSearchesTab } from '../components/market/SavedSearchesTab';
import { queryOf } from '../components/market/savedSearch';
import { FittedTable } from '../components/shelf/FittedTable';
import { ShelfGrid } from '../components/shelf/ShelfGrid';
import { ShelfToolbar } from '../components/shelf/ShelfToolbar';
import type { ShelfView } from '../components/shelf/ShelfToolbar';
import { fromMarketCard } from '../components/shelf/shelfCard';
import { useNearBottom } from '../hooks/useNearBottom';
import { useFillHeight } from '../hooks/useFillHeight';
import { usePageState } from '../hooks/usePageState';
import { money } from '../components/grading/money';
import type { MarketQuery, MarketResult, SavedSearch } from '../api/client';

/**
 * Two sections, not three.
 *
 * Liked cards used to be planned for here and now live on My collection:
 * liking a card is the step before owning one, and the question it answers
 * sits better beside the answer to "what did I buy". Keeping a second copy
 * of the list here would mean two places to unlike from.
 */
const SEARCH = 'search';
const SAVED = 'saved';

/** How many result images a saved search keeps to be recognised by. */
const PREVIEW_IMAGES = 4;

/** Matches RESULT_LIMIT on the hub: one window of candidates. */
const PAGE_SIZE = 24;

const MODE_LABEL: Record<string, string> = {
  BEST_MATCH: 'closest to what you typed',
  ARBITRAGE: 'ranked by what one successful grade returns',
  TOP_VALUE: 'ranked by the dearest grade that has actually sold',
};

function Blank() {
  return (
    <Box sx={{ display: 'grid', placeItems: 'center', flex: 1, minHeight: 200, px: 3 }}>
      <Stack spacing={1} alignItems="center" sx={{ textAlign: 'center', maxWidth: 520 }}>
        <SearchIcon sx={{ fontSize: 34, color: 'text.disabled' }} />
        <Typography variant="body1" color="text.secondary">
          Search for any card by name
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Sports, signed and trading cards alike. Prices come from completed sales, so a
          search reads a page per result and takes a few seconds.
        </Typography>
      </Stack>
    </Box>
  );
}

export function SearchPage() {
  // The submitted query, so coming back to the page shows the results
  // still sitting in the cache rather than an empty box.
  const [params, setParams] = usePageState<MarketQuery | null>('search.query', null);
  const [detail, setDetail] = usePageState<MarketResult | null>('search.open', null);
  // Held rather than added straight from the row: the grade decides most of
  // what a holding is worth, so it has to be asked for.
  const [adding, setAdding] = usePageState<MarketResult | null>('search.adding', null);
  const [section, setSection] = usePageState('search.section', SEARCH, 'durable');
  const [view, setView] = usePageState<ShelfView>('search.view', 'table', 'durable');
  const [expanded, setExpanded] = usePageState('search.expanded', false, 'durable');

  // The form's working copy. Held here because two controls share it - the
  // query bar and the filter popover - and they submit together.
  const [draft, setDraft] = useSearchDraft(params);

  const theme = useTheme();
  const onDesktop = useMediaQuery(theme.breakpoints.up('md'));
  // Measured, not guessed: the room under the table changes when the
  // figures above it are hidden, and a constant allowance cannot know.
  const [fillRef, tableHeight] = useFillHeight();

  const search = useMarketSearch(params);
  const add = useAddToCollection();
  const addWithTags = useAddHoldingWithTags();
  const saved = useSaved();
  const likedKeys = new Set((saved.data?.likes ?? []).map((row) => row.product_key));
  const watchedKeys = new Set((saved.data?.watching ?? []).map((row) => row.product_key));
  const toggleLike = useToggleLike(likedKeys);
  // Offered straight after a card is kept, while it is still in mind.
  // Only on the way in: unliking a card is not a moment to ask which
  // run it belongs to.
  const [filing, setFiling] = useState<TaggableCard | null>(null);
  const [justLiked, setJustLiked] = useState(false);
  const likeThen = (row: { key: string; name: string }) => {
    const wasLiked = likedKeys.has(row.key);
    toggleLike.mutate(row.key);
    if (!wasLiked) {
      setJustLiked(true);
      setFiling({ key: row.key, name: row.name, kept: true, tags: [] });
    }
  };
  const toggleWatch = useToggleWatch(watchedKeys);
  const saveSearch = useSaveSearch();
  const forgetSearch = useForgetSearch();
  const markRun = useMarkSearchRun();
  const searches = saved.data?.searches ?? [];

  // Every page fetched so far, in order. The table grows rather than
  // paging, so what is on screen is what the search has found.
  // Memoised because the tiles and the key lookup derive from it: a fresh
  // array every render would rebuild both on every render.
  const rows = useMemo(
    () => search.data?.pages.flatMap((page) => page.results) ?? [],
    [search.data],
  );
  const latest = search.data?.pages.at(-1);
  const cards = useMemo(() => rows.map(fromMarketCard), [rows]);
  const byKey = useMemo(() => new Map(rows.map((row) => [row.key, row])), [rows]);
  const dearest = rows.reduce(
    (top: number, row: MarketResult) => Math.max(top, Number(row.best_price ?? 0)),
    0,
  );
  const profitable = rows.filter(
    (row: MarketResult) => Number(row.best_case_profit ?? '0') > 0,
  ).length;
  const sports = rows.filter((row: MarketResult) => row.category === 'SPORTS').length;

  // How far this window reaches, and what is left behind it. The
  // catalogue hands back every reference it found; a page reads a
  // couple of dozen of them.
  const more = Math.max((latest?.candidates ?? 0) - rows.length, 0);

  // Load the next page as the end comes into view, rather than asking for a
  // click per twenty-four cards. Guarded on `hasNextPage` and on not already
  // fetching, so a fast scroll asks once rather than for every scroll event.
  const endRef = useRef<HTMLDivElement | null>(null);
  const loadMore = useCallback(() => {
    if (search.hasNextPage && !search.isFetchingNextPage) void search.fetchNextPage();
  }, [search]);
  useNearBottom(endRef, loadMore, Boolean(search.hasNextPage));

  const submit = (next: SearchDraft) => {
    setDraft(next);
    setParams(queryFrom(next));
  };

  /**
   * Save the query together with a few of the cards it found.
   *
   * The images come from the results already on screen rather than from a
   * fresh search, so saving costs nothing. They are a snapshot of that
   * moment and the saved list says so.
   */
  const save = () => {
    if (!params) return;
    saveSearch.mutate({
      ...params,
      preview_images: rows
        .map((row: MarketResult) => row.image_url)
        .filter((url: string | null): url is string => Boolean(url))
        .slice(0, PREVIEW_IMAGES),
      result_count: rows.length,
    });
  };

  /**
   * Run a kept search: show its results, and record that it ran.
   *
   * The stamp is what makes the "last run" column true. Only saved rows
   * get it - a phrase typed by hand is not this saved search even when the
   * words match.
   */
  const run = (entry: SavedSearch) => {
    setParams(queryOf(entry));
    setSection(SEARCH);
    markRun.mutate(entry.id);
  };

  const tabs = [
    { value: SEARCH, label: 'Search' },
    {
      value: SAVED,
      label: searches.length > 0 ? `Saved searches (${searches.length})` : 'Saved searches',
    },
  ];

  const results =
    search.data === undefined && !search.isFetching ? (
      <Blank />
    ) : view === 'grid' ? (
      <ShelfGrid
        cards={cards}
        onOpen={(card) => setDetail(byKey.get(card.key) ?? null)}
        emptyMessage={search.isFetching ? 'Reading prices' : 'Nothing matched that search.'}
      />
    ) : (
      <FittedTable fillRef={fillRef}>
        <MarketTable
          rows={rows}
          loading={search.isFetching}
          busy={add.isPending}
          onOpen={setDetail}
          onAdd={setAdding}
          onLike={likeThen}
          liked={likedKeys}
          watched={watchedKeys}
          onWatch={(row) => toggleWatch.mutate(row.key)}
          maxHeight={onDesktop ? tableHeight : 0}
        />
      </FittedTable>
    );

  return (
    <PageContainer
      disableBodyScroll={onDesktop}
      tabs={<PageTabs tabs={tabs} value={section} onChange={setSection} />}
    >
      <Stack spacing={1.5} sx={{ minWidth: 0, height: '100%' }}>
        {search.error ? (
          <Alert severity="error">{(search.error as Error).message}</Alert>
        ) : null}
        {add.error ? <Alert severity="error">{(add.error as Error).message}</Alert> : null}
        {add.isSuccess ? (
          <Alert severity="success" onClose={() => add.reset()}>
            Added to your collection.
          </Alert>
        ) : null}

        {/* Hidden while expanded, and absent before a search: the figures
            describe results, and four empty boxes describe nothing. */}
        {search.data && !expanded && section === SEARCH ? (
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
              flexShrink: 0,
            }}
          >
            <MetricCard
              label="Found"
              value={String(rows.length)}
              helper={
                (latest?.filtered_out ?? 0) > 0
                  ? `${latest?.filtered_out ?? 0} filtered out`
                  : MODE_LABEL[latest?.mode ?? 'BEST_MATCH']
              }
            />
            <MetricCard
              label="Dearest"
              value={dearest > 0 ? money(String(dearest)) : '—'}
              helper="best grade sold"
            />
            <MetricCard
              label="Pay to grade"
              value={String(profitable)}
              helper="profit if one gems"
            />
            <MetricCard
              label="Sports"
              value={`${sports}/${rows.length}`}
              helper="rest are trading cards"
            />
          </Box>
        ) : null}

        {/* The controls get the whole row under the figures, rather than
            sharing one with the tabs - the query box is the widest thing
            on the page and was being squeezed into what the tab strip
            left over. */}
        {section === SEARCH ? (
          <ShelfToolbar view={view} onView={setView} expanded={expanded} onExpanded={setExpanded}>
            <SearchBar
              draft={draft}
              onChange={setDraft}
              onSubmit={submit}
              busy={search.isFetching}
              suggestions={searches}
              onSave={params ? save : undefined}
              saving={saveSearch.isPending}
            />
            <SearchFilterButton
              draft={draft}
              onChange={setDraft}
              onApply={() => submit(draft)}
              busy={search.isFetching}
            />
          </ShelfToolbar>
        ) : null}

        {section === SAVED ? (
          <SavedSearchesTab
            searches={searches}
            busy={forgetSearch.isPending}
            onRun={run}
            onForget={(searchId) => forgetSearch.mutate(searchId)}
            maxHeight={onDesktop ? tableHeight : 0}
          />
        ) : (
          <>
            {results}

            {/* Fetched as the end approaches rather than on a click.
                A later window costs only the next slice of the same
                candidate list, which was found in one pass. */}
            <Box ref={endRef} sx={{ flexShrink: 0 }}>
              {search.isFetchingNextPage ? (
                <Stack direction="row" justifyContent="center" sx={{ py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Loading more
                  </Typography>
                </Stack>
              ) : more > 0 ? (
                <Stack direction="row" justifyContent="center" sx={{ py: 0.5 }}>
                  <Button size="small" variant="text" onClick={loadMore}>
                    {`Show ${Math.min(more, PAGE_SIZE)} more`}
                  </Button>
                </Stack>
              ) : null}
            </Box>
          </>
        )}
      </Stack>

      <MarketCardDialog
        card={detail}
        moreActions={
          detail ? (
            <Button
              size="small"
              onClick={() => {
                setJustLiked(false);
                setFiling({
                  key: detail.key,
                  name: detail.name,
                  kept: likedKeys.has(detail.key),
                  tags:
                    (saved.data?.likes ?? []).find((row) => row.product_key === detail.key)
                      ?.tags ?? [],
                });
              }}
            >
              Add to sub-collection
            </Button>
          ) : null
        }
        onClose={() => setDetail(null)}
        onAdd={(card) => {
          setDetail(null);
          setAdding(card);
        }}
      />

      <CardTagDialog card={filing} justLiked={justLiked} onClose={() => setFiling(null)} />

      <AddHoldingDialog
        card={adding}
        busy={add.isPending}
        onClose={() => setAdding(null)}
        onAdd={(body, tagIds) => {
          addWithTags.mutate({ body, tagIds });
          setAdding(null);
        }}
      />
    </PageContainer>
  );
}
